import { useEffect, useState } from 'react'
import { Shield } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import type { EgressPolicy, EgressPolicyLevel } from '@/service/api'
import { Label } from '@/components/ui/label'

/**
 * Admin control for the per-tenant egress-consent policy (Trust/Security §9
 * "Fallback consent UX"): whether the agent may reach public egress surfaces
 * (web search / public-model inference) without asking. "Ask" pauses each
 * conversation's first would-be web egress behind an in-chat consent prompt
 * (FS-676 — docs/egress-consent-design.md); it stays disabled for the
 * public-model select, whose enforcement lands with the private-model
 * fallback work (INFRA-165).
 *
 * Native <select> (not the Radix Select) so the control saves on change and
 * stays directly testable; visual classes mirror the ui/select trigger.
 */

type PolicyField = 'webSearchPolicy' | 'publicModelPolicy'

const SELECT_CLASSES =
  'h-9 w-56 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm ' +
  'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

export default function EgressPolicyControl() {
  const { t } = useTranslation()
  const [policy, setPolicy] = useState<EgressPolicy | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<PolicyField | null>(null)
  // Translated at RENDER time so the load effect has no dependency on `t`
  // (an unstable t identity re-running the effect would re-fetch and clobber
  // a just-saved policy).
  const [error, setError] = useState<'load' | 'save' | null>(null)

  useEffect(() => {
    let cancelled = false
    apiService
      .getEgressPolicy()
      .then((p) => {
        if (!cancelled) setPolicy(p)
      })
      .catch((err) => {
        console.error('Error loading egress policy:', err)
        if (!cancelled) setError('load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // The last attempted change, kept so a failed save can be retried. Without it
  // the only recovery is re-picking the same option — which a native <select>
  // won't even fire an onChange for, since the value reverted underneath it.
  const [lastAttempt, setLastAttempt] = useState<{
    field: PolicyField
    value: EgressPolicyLevel
  } | null>(null)

  const handleChange = async (field: PolicyField, value: EgressPolicyLevel) => {
    if (!policy) return
    const previous = policy
    setPolicy({ ...policy, [field]: value })
    setSaving(field)
    setError(null)
    setLastAttempt({ field, value })
    try {
      const saved = await apiService.updateEgressPolicy({ [field]: value })
      setPolicy(saved)
      setLastAttempt(null)
    } catch (err) {
      console.error('Error updating egress policy:', err)
      setPolicy(previous)
      setError('save')
    } finally {
      setSaving(null)
    }
  }

  const errorMessage =
    error === 'load'
      ? t('common:settings.egress_policy.error_loading', 'Could not load the egress policy.')
      : error === 'save'
        ? t('common:settings.egress_policy.error_saving', 'Could not save the egress policy.')
        : null

  // "Ask" is live for web search (FS-676 in-chat consent prompt) but not for
  // public-model calls — that policy is stored-only until the private-model
  // fallback ships, and offering "ask" on an unenforced surface would promise
  // a prompt that never appears.
  const optionsFor = (
    field: PolicyField
  ): Array<{ value: EgressPolicyLevel; label: string; disabled?: boolean }> => [
    { value: 'always_allow', label: t('common:settings.egress_policy.always_allow', 'Always allow') },
    field === 'webSearchPolicy'
      ? { value: 'ask', label: t('common:settings.egress_policy.ask_live', 'Ask the user') }
      : { value: 'ask', label: t('common:settings.egress_policy.ask', 'Ask (coming soon)'), disabled: true },
    { value: 'always_deny', label: t('common:settings.egress_policy.always_deny', 'Always deny') },
  ]

  const renderSelect = (field: PolicyField, id: string, label: string, description: string) => (
    <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
      <div className="space-y-1 min-w-0">
        <Label htmlFor={id} className="text-base font-medium">
          {label}
        </Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <select
        id={id}
        data-testid={id}
        className={SELECT_CLASSES}
        value={policy?.[field] ?? 'always_allow'}
        // Only the field being saved locks — the two policies are independent,
        // so an in-flight web-search save shouldn't freeze the public-model select.
        disabled={loading || saving === field || !policy}
        onChange={(e) => handleChange(field, e.target.value as EgressPolicyLevel)}
      >
        {optionsFor(field).map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )

  return (
    <div className="border rounded-lg px-4 py-3 space-y-3" data-testid="egress-policy-control">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 shrink-0" />
        <div>
          <span className="font-medium">
            {t('common:settings.egress_policy.title', 'External data egress')}
          </span>
          <p className="text-sm text-muted-foreground">
            {t(
              'common:settings.egress_policy.description_v2',
              'Whether the agent may send data to services outside your deployment. "Ask" pauses the first would-be web search in each conversation behind a consent prompt.'
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-2" data-testid="egress-policy-loading">
          <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-sm text-muted-foreground">
            {t('common:general.loading', 'Loading…')}
          </span>
        </div>
      ) : policy ? (
        <>
          {renderSelect(
            'webSearchPolicy',
            'egress-web-search-policy',
            t('common:settings.egress_policy.web_search', 'Web search'),
            t(
              'common:settings.egress_policy.web_search_description',
              'Public web lookups when plant data cannot answer a question.'
            )
          )}
          {renderSelect(
            'publicModelPolicy',
            'egress-public-model-policy',
            t('common:settings.egress_policy.public_models', 'Public model calls'),
            t(
              'common:settings.egress_policy.public_models_description',
              'Recorded for upcoming private-model fallback; not enforced yet.'
            )
          )}
        </>
      ) : null}

      {errorMessage && (
        // role="alert" matters more than usual here: the failure mode is
        // "you think you turned egress off but didn't", and the select has
        // already reverted, so a silent banner is the only remaining signal.
        <p
          role="alert"
          className="text-sm text-red-600 dark:text-red-400"
          data-testid="egress-policy-error"
        >
          {errorMessage}
          {error === 'save' && lastAttempt && (
            <button
              type="button"
              data-testid="egress-policy-retry"
              className="ml-2 underline underline-offset-2 hover:no-underline disabled:opacity-50"
              disabled={saving !== null}
              onClick={() => void handleChange(lastAttempt.field, lastAttempt.value)}
            >
              {t('common:settings.egress_policy.retry', 'Retry')}
            </button>
          )}
        </p>
      )}
    </div>
  )
}
